import { useEffect, useState } from "react";
import { salonService } from "../../services/salonService";
import { subscriptionService } from "../../services/subscriptionService";
import Icon from "../../components/Common/Icon";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { useToast } from "../../context/ToastContext";

const EMPTY = {
  managerEmail: "",
  managerFullName: "",
  managerPhone: "",
  packageId: "",
  licenseMonths: "12",
  salonName: "",
  taxNumber: "",
  salonPhone: "",
  salonEmail: "",
  website: "",
  address: "",
  description: "",
};

export default function SalonOnboardingPage() {
  const { showToast } = useToast();
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // { salon, managerEmail, invitationExpiresAt }
  const [packagesError, setPackagesError] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let active = true;
    subscriptionService
      .list()
      .then((res) => {
        if (!active) return;
        setPackages(res.data.data ?? []);
        setPackagesError(null);
      })
      .catch((err) => {
        if (!active) return;
        setPackages([]);
        setPackagesError(err.response?.data?.error ?? err.message ?? "Paketler yüklenemedi");
      });
    return () => {
      active = false;
    };
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    setConfirmOpen(true);
  };

  const handleConfirmOnboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await salonService.onboard(form);
      setResult(res.data.data);
      setForm(EMPTY);
      showToast("Salon onboarding başarıyla tamamlandı. Davet gönderildi.", "success");
    } catch (err) {
      const errMsg = err.response?.data?.error ?? "Onboarding başarısız";
      setError(errMsg);
      showToast(errMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  // Başarı ekranı: manager'a aktivasyon (davet) e-postası gönderildi
  if (result) {
    const expires = result.invitationExpiresAt
      ? new Date(result.invitationExpiresAt).toLocaleString("tr-TR")
      : "—";
    return (
      <div className="max-w-xl mx-auto">
        <div className="bg-surface-card rounded-3xl shadow-card border border-outline-variant p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-success/15 text-success flex items-center justify-center mx-auto mb-4">
            <Icon name="mark_email_read" filled className="!text-[32px]" />
          </div>
          <h2 className="text-2xl font-bold text-on-surface">Davet Gönderildi</h2>
          <p className="text-on-surface-variant mt-1">
            <strong>{result.salon.name}</strong> · Lisans {result.salon.licenseNo}
          </p>
 
          <div className="mt-6 bg-surface-container-low rounded-2xl p-5 text-left space-y-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-on-surface-variant mb-1">
                Manager E-posta
              </p>
              <p className="font-semibold text-on-surface">{result.managerEmail}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-on-surface-variant mb-1">
                Davet Geçerlilik Süresi
              </p>
              <p className="font-semibold text-on-surface">{expires}</p>
            </div>
          </div>
 
          <div className="mt-4 flex items-start gap-2 text-left text-sm text-on-surface-variant bg-tier-gold/10 rounded-xl p-3">
            <Icon name="info" className="!text-[18px] text-tier-gold" />
            <span>
              Manager'a aktivasyon bağlantısı e-postayla gönderildi. Şifreyi kendisi
              belirler; admin şifreyi görmez.
            </span>
          </div>
 
          <button
            onClick={() => setResult(null)}
            className="mt-6 w-full bg-primary text-white rounded-xl py-3 font-semibold hover:opacity-90 transition active:scale-[0.99]"
          >
            Yeni Salon Ekle
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Icon name="add_business" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-on-surface">Salon Onboarding</h1>
          <p className="text-on-surface-variant text-sm">
            Yeni salon oluştur ve yöneticisini (manager) tanımla. Şifre otomatik üretilir.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-error bg-error/10 rounded-xl px-4 py-3">
          <Icon name="error" className="!text-[18px]" /> {error}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        {/* Manager */}
        <Section title="Yönetici (Manager)" icon="person">
          <Field label="Ad Soyad" name="managerFullName" value={form.managerFullName} onChange={onChange} required />
          <Field label="E-posta" name="managerEmail" type="email" value={form.managerEmail} onChange={onChange} required />
          <Field label="Telefon" name="managerPhone" value={form.managerPhone} onChange={onChange} placeholder="+9055..." />
        </Section>

        {/* Salon */}
        <Section title="Salon Bilgileri" icon="storefront">
          <Field label="Salon Adı" name="salonName" value={form.salonName} onChange={onChange} required />
          <Field label="Salon Vergi Numarası (İsteğe Bağlı)" name="taxNumber" value={form.taxNumber} onChange={onChange} placeholder="1234567890" />
          <div>
            <label className="block text-sm font-medium mb-1.5 text-on-surface-variant">Paket</label>
            <select
              name="packageId"
              value={form.packageId}
              onChange={onChange}
              required
              className="w-full border border-outline-variant rounded-xl px-3.5 py-2.5 bg-white focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
            >
              <option value="">Seçin…</option>
              {packages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.monthlyPrice}₺/ay
                </option>
              ))}
            </select>
            {packagesError && (
              <p className="mt-1 text-xs text-error flex items-center gap-1">
                <Icon name="error" className="!text-[14px]" /> Paketler yüklenemedi: {packagesError}
              </p>
            )}
            {!packagesError && packages.length === 0 && (
              <p className="mt-1 text-xs text-on-surface-variant">Paketler yükleniyor…</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5 text-on-surface-variant">Lisans Süresi</label>
            <select
              name="licenseMonths"
              value={form.licenseMonths}
              onChange={onChange}
              className="w-full border border-outline-variant rounded-xl px-3.5 py-2.5 bg-white focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition font-medium"
            >
              <option value="0">7 Günlük Test / Deneme Süresi</option>
              <option value="1">1 ay</option>
              <option value="3">3 ay</option>
              <option value="6">6 ay</option>
              <option value="12">12 ay (1 yıl)</option>
            </select>
          </div>
          <Field label="Salon Telefonu" name="salonPhone" value={form.salonPhone} onChange={onChange} />
          <Field label="Salon E-postası" name="salonEmail" type="email" value={form.salonEmail} onChange={onChange} />
          <Field label="Web Sitesi" name="website" value={form.website} onChange={onChange} placeholder="https://..." />
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1.5 text-on-surface-variant">Açık Adres</label>
            <textarea
              name="address"
              rows={2}
              value={form.address}
              onChange={onChange}
              placeholder="Mahalle, Cadde, Sokak, No, İl/İlçe..."
              className="w-full border border-outline-variant rounded-xl px-3.5 py-2.5 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition text-sm"
            />
          </div>
        </Section>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary text-white rounded-xl py-3.5 font-semibold hover:opacity-90 transition active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? "Oluşturuluyor…" : (<><Icon name="rocket_launch" /> Salonu Oluştur</>)}
        </button>
      </form>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmOpen}
        title="Salon Kurulum Onayı"
        message="Yeni bir salon onboarding kurulumu başlatmak ve yöneticisine aktivasyon e-postası göndermek istediğinize emin misiniz?"
        type="info"
        onConfirm={handleConfirmOnboard}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-surface-card rounded-3xl shadow-card border border-outline-variant p-6">
      <h2 className="flex items-center gap-2 font-semibold text-on-surface mb-4">
        <Icon name={icon} className="!text-[20px] text-primary" /> {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5 text-on-surface-variant">{label}</label>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full border border-outline-variant rounded-xl px-3.5 py-2.5 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
      />
    </div>
  );
}
